/**
 * One cohort in the Rosters table: the summary row (name, member count,
 * pairing pills, actions) plus the expanded detail row with per-pairing
 * breakdowns. Presentation only — every action is a callback.
 */
import type { AssignmentSlice } from '@/lib/cohortEntity';
import type { CohortEntityRow, PairingRow } from '@/hooks/useCohortEntities';
import type { GroupRunRowData } from '@/hooks/useGroupRuns';
import { PairingStatusPill } from './atoms';
import { PairingDetail, type GroupRunActions } from './PairingDetail';

export function CohortRow({
  cohort: c,
  expanded,
  onToggle,
  busy,
  orientationNameById,
  assignmentSlices,
  groupRunsByPairing,
  currentUserEmail,
  runActions,
  onCreateGroup,
  onRename,
  onEditMembers,
  onArchive,
  onCancelPairing,
}: {
  cohort: CohortEntityRow;
  expanded: boolean;
  onToggle: () => void;
  busy: boolean;
  orientationNameById: Map<string, string>;
  assignmentSlices: AssignmentSlice[];
  groupRunsByPairing: Map<string, GroupRunRowData[]>;
  currentUserEmail: string;
  runActions: GroupRunActions;
  onCreateGroup: (
    pairing: PairingRow,
    assignedEmails: string[],
    blockedReasons: Map<string, string>,
  ) => void;
  onRename: () => void;
  onEditMembers: () => void;
  onArchive: () => void;
  onCancelPairing: (p: PairingRow) => void;
}) {
  const livePairings = c.pairings.filter((p) => p.storedStatus === 'Active');
  return (
    <>
      <tr
        onClick={onToggle}
        style={{ cursor: 'pointer', opacity: c.archived ? 0.55 : 1 }}
      >
        <td>
          <b>{c.name}</b>
          {c.archived ? <ArchivedTag /> : null}
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
            by {c.createdByEmail || 'unknown'}
          </div>
        </td>
        <td>{c.members.length}</td>
        <td>
          {c.pairings.length === 0 ? (
            <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
              none yet
            </span>
          ) : (
            c.pairings.map((p) => (
              <div
                key={String(p.cohortAssignmentId)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}
              >
                <span
                  style={{
                    maxWidth: 260,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 12.5,
                  }}
                >
                  {orientationNameById.get(p.orientationId) ?? p.orientationId}
                </span>
                <PairingStatusPill status={p.displayStatus} />
              </div>
            ))
          )}
        </td>
        <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
          {!c.archived ? (
            <>
              <button type="button" className="btn sm ghost" disabled={busy} onClick={onRename}>
                Rename
              </button>
              <button
                type="button"
                className="btn sm ghost"
                style={{ marginLeft: 6 }}
                disabled={busy}
                onClick={onEditMembers}
              >
                Members
              </button>
              <button
                type="button"
                className="btn sm ghost"
                style={{ marginLeft: 6 }}
                disabled={busy}
                onClick={onArchive}
              >
                Archive
              </button>
            </>
          ) : (
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>frozen</span>
          )}
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={4} style={{ background: 'var(--panel-2)' }}>
            <div style={{ padding: '10px 14px', display: 'grid', gap: 10 }}>
              {c.pairings.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                  No orientations assigned to this cohort yet. Use the
                  Orientation Assignment page with target <b>Cohort</b>.
                </div>
              ) : (
                c.pairings.map((p) => (
                  <PairingDetail
                    key={String(p.cohortAssignmentId)}
                    cohort={c}
                    pairing={p}
                    orientationName={
                      orientationNameById.get(p.orientationId) ?? p.orientationId
                    }
                    assignmentSlices={assignmentSlices}
                    groupRuns={groupRunsByPairing.get(String(p.cohortAssignmentId)) ?? []}
                    currentUserEmail={currentUserEmail}
                    runActions={runActions}
                    onCreateGroup={(emails, reasons) => onCreateGroup(p, emails, reasons)}
                    busy={busy}
                    onCancel={() => onCancelPairing(p)}
                  />
                ))
              )}
              {c.members.length > 0 && livePairings.length === 0 ? (
                <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                  Roster: {c.members.join(', ')}
                </div>
              ) : null}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function ArchivedTag() {
  return (
    <span
      style={{
        marginLeft: 6,
        fontSize: 10,
        fontWeight: 600,
        color: 'var(--muted)',
        background: 'var(--panel-3)',
        padding: '1px 6px',
        borderRadius: 8,
        textTransform: 'uppercase',
        letterSpacing: '.04em',
      }}
    >
      Archived
    </span>
  );
}
